import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageMetadata } from '../context/PageMetadataContext';
import { pageTimestamps } from '../generated/pageTimestamps';
import { ROUTE_TO_PAGE_KEY } from '../config/routeConfig';
import './LastUpdated.css';

/**
 * Reusable LastUpdated component
 *
 * Displays the last updated date for the current page. Each page can provide
 * its last updated via PAGE_LAST_UPDATED config in the page JS. If not provided,
 * falls back to the page JS file's modification timestamp (generated at build time).
 *
 * Usage in a page:
 *
 * // In page JS - export config (optional)
 * export const PAGE_LAST_UPDATED = { dateTime: '2025-12-23', displayDate: 'December 23, 2025' };
 *
 * // The page sets context on mount - LastUpdated reads it automatically
 * <LastUpdated label="Page last updated:" />
 *
 * // Direct props override (for one-off usage)
 * <LastUpdated dateTime="2025-12-23" displayDate="December 23, 2025" />
 *
 * @param {Object} props
 * @param {string} props.dateTime - ISO date string (YYYY-MM-DD) - direct override
 * @param {string} props.displayDate - Formatted date string - direct override
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.label - Label text (default: "Last updated:")
 */
const LastUpdated = ({
  dateTime,
  displayDate,
  className = '',
  label = 'Last updated:'
}) => {
  const { metadata } = usePageMetadata();
  const location = useLocation();
  const [dateInfo, setDateInfo] = useState(null);

  useEffect(() => {
    // 1. Direct props take precedence
    if (dateTime && displayDate) {
      setDateInfo({ dateTime, displayDate });
      return;
    }

    // 2. Page config from context (set by current page)
    if (metadata?.dateTime && metadata?.displayDate) {
      setDateInfo(metadata);
      return;
    }

    // 3. Fallback: page JS file timestamp from generated pageTimestamps
    const pageKey = ROUTE_TO_PAGE_KEY[location.pathname];
    if (pageKey && pageTimestamps[pageKey]) {
      setDateInfo(pageTimestamps[pageKey]);
      return;
    }

    setDateInfo(null);
  }, [dateTime, displayDate, metadata, location.pathname]);

  if (!dateInfo) {
    return null;
  }

  return (
    <p className={`update ${className}`.trim()}>
      {label}{' '}
      <time dateTime={dateInfo.dateTime}>{dateInfo.displayDate}</time>
    </p>
  );
};

export default LastUpdated;
